import * as React from 'react';
import { Plus, X } from 'lucide-react';
import { WorkspaceButton, WorkspaceNotice, WorkspaceSelect } from '@/components/ui/workspace';
import { EmptyState } from '@/components/ui/empty-state';
import { RoleChip, roleLabel } from '@/components/settings/users-table';
import { avatarTone, deriveInitials } from '@/components/clients/team-avatars';
import type { UserDirectoryItem } from '@/lib/api/users-directory';
import { cn } from '@/lib/utils/cn';

/**
 * Engagement team on the client page (T-0227 / C2-10): who is assigned,
 * with their firm role, and for firm admins and engagement leads the
 * controls to add or remove someone. Inline on the page, not a modal: pick
 * a colleague from the directory and add; remove from the row.
 *
 * A team member has no role of their own on the client (the membership
 * table carries none): the role shown is the user's firm role, which is
 * what decides what they may do here. Members who have since been
 * deactivated are not in the directory; their name comes from the
 * read-only lookup and they can still be removed.
 */

export interface TeamEditorProps {
  memberIds: ReadonlyArray<string>;
  /** Names for any member id (the lookup); the directory covers active users. */
  namesById: ReadonlyMap<string, string>;
  /** Active users with roles; undefined while loading or when the viewer may not manage. */
  directory: ReadonlyArray<UserDirectoryItem> | undefined;
  canManage: boolean;
  saving: boolean;
  /** Server refusal, if the last change failed. */
  error: string | null;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
}

export function TeamEditor({
  memberIds,
  namesById,
  directory,
  canManage,
  saving,
  error,
  onAdd,
  onRemove,
}: TeamEditorProps) {
  const [pick, setPick] = React.useState('');
  const byId = React.useMemo(
    () => new Map((directory ?? []).map((u) => [u.id, u] as const)),
    [directory],
  );
  const members = memberIds.map((id) => {
    const entry = byId.get(id);
    const name = entry?.name ?? namesById.get(id) ?? id.slice(0, 8);
    return { id, name, role: entry?.default_role ?? null, active: entry !== undefined };
  });
  const memberSet = new Set(memberIds);
  const candidates = (directory ?? []).filter((u) => !memberSet.has(u.id));

  const add = () => {
    if (!pick) return;
    onAdd(pick);
    setPick('');
  };

  return (
    <div className="flex flex-col gap-4">
      {members.length === 0 ? (
        <EmptyState
          title="No team members"
          description={
            canManage
              ? 'No firm users are assigned to this client yet. Add the engagement team below.'
              : 'No firm users are assigned to this client yet. A firm admin or an engagement lead on the team can add members.'
          }
        />
      ) : (
        <ul className="divide-y divide-ink-100" aria-label="Team members">
          {members.map((m) => {
            const tone = avatarTone({ id: m.id, name: m.name });
            return (
              <li key={m.id} className="flex items-center gap-3 py-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-paper-0 font-mono text-[11px] font-semibold',
                    `bg-avatar-${tone}-bg`,
                    `text-avatar-${tone}-ink`,
                  )}
                >
                  {deriveInitials(m.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-ink-900">
                  {m.name}
                </span>
                {m.role ? (
                  <RoleChip role={m.role} />
                ) : directory ? (
                  <span className="text-meta text-ink-500">Deactivated</span>
                ) : null}
                {canManage ? (
                  <WorkspaceButton
                    variant="tertiary"
                    onClick={() => onRemove(m.id)}
                    disabled={saving}
                    aria-label={`Remove ${m.name} from the team`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Remove
                  </WorkspaceButton>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canManage ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-ink-100 pt-4">
          <label className="min-w-0 flex-1 text-meta font-medium text-ink-700">
            Add a firm user
            <WorkspaceSelect
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              disabled={saving || directory === undefined}
              className="mt-1.5"
            >
              <option value="">
                {directory === undefined
                  ? 'Loading users…'
                  : candidates.length === 0
                    ? 'Every active user is on the team'
                    : 'Choose a user…'}
              </option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {roleLabel(u.default_role)}
                </option>
              ))}
            </WorkspaceSelect>
          </label>
          <WorkspaceButton variant="secondary" onClick={add} disabled={!pick || saving}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add to team
          </WorkspaceButton>
        </div>
      ) : null}

      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </div>
  );
}
